/*
 * DealerManualLangBadge — call-out card that surfaces the operational
 * manual in the dealer's native language.
 *
 * Fabrizio CR 04/07/2026 — after extending the operational manual to
 * all 18 SOC languages (iteration 73/74), many non-Italian dealers still
 * default to English simply because they don't know a native version
 * exists. This badge is a low-friction discovery aid: shows the manual's
 * language IN that language (e.g. "Ελληνικά" for a Greek dealer) so it
 * jumps out visually without requiring UI-wide translation strings.
 *
 * Hidden for:
 *   - IT dealers (already the master language, no discovery gap)
 *   - Dealers with unknown/missing country
 */
import { BookOpen, ArrowRight } from "lucide-react";
import PropTypes from "prop-types";

// Kept in sync with backend `_LOCALIZED_SVG_LANGS` and
// `translations/dealerPortal.js::LANGUAGE_OPTIONS`. The label is written
// in the language ITSELF (e.g. "Ελληνικά" for Greek) so the dealer
// recognises it without needing to speak Italian/English first.
const LANG_NATIVE = {
  UK: { flag: "🇬🇧", native: "English" },
  FR: { flag: "🇫🇷", native: "Français" },
  ES: { flag: "🇪🇸", native: "Español" },
  DE: { flag: "🇩🇪", native: "Deutsch" },
  PL: { flag: "🇵🇱", native: "Polski" },
  PT: { flag: "🇵🇹", native: "Português" },
  NL: { flag: "🇳🇱", native: "Nederlands" },
  BE: { flag: "🇧🇪", native: "Français (BE)" },
  SE: { flag: "🇸🇪", native: "Svenska" },
  NO: { flag: "🇳🇴", native: "Norsk" },
  FI: { flag: "🇫🇮", native: "Suomi" },
  DK: { flag: "🇩🇰", native: "Dansk" },
  CZ: { flag: "🇨🇿", native: "Čeština" },
  GR: { flag: "🇬🇷", native: "Ελληνικά" },
  CH: { flag: "🇨🇭", native: "Deutsch (CH)" },
  GBZ: { flag: "🇬🇮", native: "English (GBZ)" },
  IRL: { flag: "🇮🇪", native: "English (IRL)" },
};

// Bilingual label ("Manual available in your language" written both in
// English AND the dealer's own language). English is the universally
// understood second layer for all our non-IT SOC markets.
const BILINGUAL_LABEL = {
  UK: "Operational manual · in your language",
  FR: "Manuel opérationnel · dans votre langue",
  ES: "Manual operativo · en tu idioma",
  DE: "Betriebshandbuch · in Ihrer Sprache",
  PL: "Instrukcja operacyjna · w Twoim języku",
  PT: "Manual operacional · no seu idioma",
  NL: "Operationele handleiding · in uw taal",
  BE: "Manuel opérationnel · dans votre langue",
  SE: "Operativ manual · på ditt språk",
  NO: "Operativ manual · på ditt språk",
  FI: "Käyttöohje · omalla kielelläsi",
  DK: "Operationel manual · på dit sprog",
  CZ: "Provozní příručka · ve vašem jazyce",
  GR: "Επιχειρησιακό εγχειρίδιο · στη γλώσσα σας",
  CH: "Betriebshandbuch · in Ihrer Sprache",
  GBZ: "Operational manual · in your language",
  IRL: "Operational manual · in your language",
};

const CTA_LABEL = {
  UK: "Open manual", FR: "Ouvrir le manuel", ES: "Abrir manual",
  DE: "Handbuch öffnen", PL: "Otwórz instrukcję", PT: "Abrir manual",
  NL: "Handleiding openen", BE: "Ouvrir le manuel", SE: "Öppna manualen",
  NO: "Åpne manualen", FI: "Avaa ohje", DK: "Åbn manualen",
  CZ: "Otevřít příručku", GR: "Άνοιγμα εγχειριδίου",
  CH: "Handbuch öffnen", GBZ: "Open manual", IRL: "Open manual",
};

const DealerManualLangBadge = ({ dealerCountry }) => {
  // Hide for IT (master language, no discovery gap) and for unknown
  // countries — never show the badge with a broken lang code.
  if (!dealerCountry || dealerCountry === "IT") return null;
  const meta = LANG_NATIVE[dealerCountry];
  if (!meta) return null;

  const href = `/dealer/manual?lang=${dealerCountry}`;
  const bilingual = BILINGUAL_LABEL[dealerCountry] || BILINGUAL_LABEL.UK;
  const cta = CTA_LABEL[dealerCountry] || CTA_LABEL.UK;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="wallet-card mt-4 flex items-center justify-between gap-4 hover:border-lojack-clause-500/40 transition-colors group"
      data-testid="dealer-manual-lang-badge"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-lojack-clause-500/15 border border-lojack-clause-500/30 flex items-center justify-center shrink-0">
          <BookOpen size={18} className="text-lojack-clause-400" />
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-medium flex items-center gap-2 flex-wrap">
            <span className="text-2xl leading-none" aria-hidden="true">{meta.flag}</span>
            <span className="text-lojack-clause-300">{meta.native}</span>
            <span className="text-gray-600 text-xs">·</span>
            <span className="text-gray-400 text-xs sm:text-sm font-normal">{bilingual}</span>
          </p>
          <p className="text-gray-500 text-xs mt-0.5">
            {/* Manual available */} PDF + web · v1.0
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-lojack-clause-300 text-sm font-medium shrink-0 group-hover:translate-x-0.5 transition-transform">
        <span className="hidden sm:inline">{cta}</span>
        <ArrowRight size={16} />
      </div>
    </a>
  );
};

DealerManualLangBadge.propTypes = {
  dealerCountry: PropTypes.string,
};

export default DealerManualLangBadge;
